import { useEffect, useMemo, useState } from 'react'

import { useFetchObjectDataWithFixedKey } from '@components/DisplayForm/hooks/useFetchObjectDataWithFixedKey'
import { useFetchPreFillObjectDataWithFixedKey } from '@components/DisplayForm/hooks/useFetchPreFillObjectDataWithFixedKey'

import {
  getCommandRequestData,
  getReadObjectCommandConfig,
  prepareRequestParamsWithExpands,
} from '@helpers'
import { FIELD_VALUE_TYPE, FORM_TYPE, PreFillType } from '@constants'
import { FormType, GETObjectModel, RequestParams } from '@types'

import { getExpandsParamForForm } from '../helpers'
import { ExecuteCommandGetArgs } from '../types'

type UseFetchRowProps = {
  type: FORM_TYPE
  formCode: string
  objectId?: string
  globalId: string
  preFill?: PreFillType
  hasPreFillField: boolean
  typeId?: FIELD_VALUE_TYPE
  formConfig?: FormType
  objectModel?: GETObjectModel
}

export const useFetchRow = ({
  type,
  objectId,
  formCode,
  globalId,
  preFill,
  hasPreFillField,
  typeId,
  formConfig,
  objectModel,
}: UseFetchRowProps) => {
  const [isLoadingPrefillRow, setIsLoadingPrefillRow] = useState(true)
  const [isLoadingRow, setIsLoadingRow] = useState(false)

  const [fetchRow] = useFetchObjectDataWithFixedKey(globalId, typeId, formConfig?.fields)
  const [fetchPreFillRow] = useFetchPreFillObjectDataWithFixedKey(globalId)

  const preFillObjectId = preFill?.objectId
  const preFillObjectCode = preFill?.objectCode

  const formObjectCode = formConfig?.objectCode

  useEffect(() => {
    // Если есть preFill поля, загружаем данные для предварительного заполнения
    if (hasPreFillField && type === FORM_TYPE.CREATE) {
      setIsLoadingPrefillRow(true)

      fetchPreFillRow({
        formCode,
        objectId: preFillObjectId,
        objectCode: preFillObjectCode,
      }).finally(() => setIsLoadingPrefillRow(false))

      return
    }

    if (formConfig) {
      setIsLoadingPrefillRow(false)
    }
  }, [formCode, preFillObjectId, preFillObjectCode, hasPreFillField, type, formConfig])

  const fetchConfig = useMemo<ExecuteCommandGetArgs | null>(() => {
    if (
      !objectId ||
      type === FORM_TYPE.CREATE ||
      !formObjectCode ||
      !formConfig ||
      formConfig.code !== formCode
    ) {
      return null
    }

    const readCommandConfig = getReadObjectCommandConfig({
      objectId,
      typeId,
      overrideCommands: formConfig.commands,
      objectCommands: objectModel?.commands,
    })

    if (!readCommandConfig) {
      return null
    }

    const { command: readCommand, sourceParams, defaultParams } = readCommandConfig

    const computedParams: RequestParams = readCommand.parameters?.length
      ? getCommandRequestData(readCommand.parameters, sourceParams)
      : readCommand.isOverridden
      ? {}
      : defaultParams

    const computedBody = readCommand.body?.length
      ? getCommandRequestData(readCommand.body, sourceParams)
      : undefined

    const formExpands = getExpandsParamForForm(formConfig.fields || [])
    const { requestParams, expands } = prepareRequestParamsWithExpands(computedParams, formExpands)

    return {
      method: readCommand.httpMethod,
      objectCode: readCommand.objectCode || formObjectCode,
      commandCode: readCommand.commandCode,
      params: requestParams,
      body: computedBody,
      resource: readCommand.resource,
      expands,
    }
  }, [objectId, type, formObjectCode, formConfig, formCode, typeId, objectModel?.commands])

  useEffect(() => {
    if (!fetchConfig) {
      return
    }

    setIsLoadingRow(true)

    fetchRow(fetchConfig).finally(() => setIsLoadingRow(false))
  }, [fetchConfig, fetchRow])

  return {
    isLoadingPrefillRow,
    isLoadingRow,
  }
}
